import React from 'react';
import { TrendingDown, Receipt, PieChart as PieChartIcon } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  BarChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { COLORS, KPICard, CustomTooltip, StatementHeader } from './ReportHelpers';

export function ExpenseReport({ reportData, viewMode, entityName }) {
  if (!reportData || !reportData.categories) return null;
  const { categories, monthly_trend, top_merchants, grand_total, transaction_count } = reportData;

  if (viewMode === 'list') {
    return (
      <Card data-testid="exp-list-view">
        <CardContent className="py-8 px-6 max-w-3xl mx-auto">
          <StatementHeader entityName={entityName} title="Expense Analysis" period={reportData.period} />
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y-2 border-zinc-800 bg-zinc-50/50">
                <th className="py-2 px-4 text-left text-xs font-semibold uppercase tracking-wider text-zinc-600">Category</th>
                <th className="py-2 px-4 text-right text-xs font-semibold uppercase tracking-wider text-zinc-600">#</th>
                <th className="py-2 px-4 text-right text-xs font-semibold uppercase tracking-wider text-zinc-600">%</th>
                <th className="py-2 px-4 text-right text-xs font-semibold uppercase tracking-wider text-zinc-600 pr-6">Amount</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(c => (
                <tr key={c.code} className="border-b border-zinc-200 hover:bg-zinc-50/50">
                  <td className="py-2 px-4 text-sm text-zinc-700">{c.code}  {c.name}</td>
                  <td className="py-2 px-4 text-right font-mono text-sm text-zinc-500">{c.count}</td>
                  <td className="py-2 px-4 text-right font-mono text-sm text-zinc-500">{c.percentage}%</td>
                  <td className="py-2 px-4 text-right font-mono text-sm tabular-nums text-zinc-900 pr-6">{formatCurrency(c.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-zinc-900 border-b-4 border-double border-zinc-900 bg-zinc-100">
                <td className="py-2 px-4 font-semibold text-sm">TOTAL EXPENSES</td>
                <td className="py-2 px-4 text-right font-mono text-sm font-semibold">{transaction_count}</td>
                <td className="py-2 px-4 text-right font-mono text-sm font-semibold">100%</td>
                <td className="py-2 px-4 text-right font-mono text-sm tabular-nums font-semibold pr-6">{formatCurrency(grand_total)}</td>
              </tr>
            </tfoot>
          </table>
          {top_merchants.length > 0 && (
            <>
              <h3 className="mt-8 mb-3 text-sm font-semibold text-zinc-500 uppercase tracking-wider">Top Merchants</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-y-2 border-zinc-800 bg-zinc-50/50">
                    <th className="py-2 px-4 text-left text-xs font-semibold uppercase tracking-wider text-zinc-600">Merchant</th>
                    <th className="py-2 px-4 text-right text-xs font-semibold uppercase tracking-wider text-zinc-600">#</th>
                    <th className="py-2 px-4 text-right text-xs font-semibold uppercase tracking-wider text-zinc-600 pr-6">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {top_merchants.map(m => (
                    <tr key={m.name} className="border-b border-zinc-200 hover:bg-zinc-50/50">
                      <td className="py-2 px-4 text-sm text-zinc-700">{m.name}</td>
                      <td className="py-2 px-4 text-right font-mono text-sm text-zinc-500">{m.count}</td>
                      <td className="py-2 px-4 text-right font-mono text-sm tabular-nums text-zinc-900 pr-6">{formatCurrency(m.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {monthly_trend.length > 0 && (
            <>
              <h3 className="mt-8 mb-3 text-sm font-semibold text-zinc-500 uppercase tracking-wider">Monthly Summary</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-y-2 border-zinc-800 bg-zinc-50/50">
                    <th className="py-2 px-4 text-left text-xs font-semibold uppercase tracking-wider text-zinc-600">Month</th>
                    <th className="py-2 px-4 text-right text-xs font-semibold uppercase tracking-wider text-zinc-600 pr-6">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {monthly_trend.map(m => (
                    <tr key={m.month} className="border-b border-zinc-200 hover:bg-zinc-50/50">
                      <td className="py-2 px-4 text-sm text-zinc-700">{m.month}</td>
                      <td className="py-2 px-4 text-right font-mono text-sm tabular-nums text-zinc-900 pr-6">{formatCurrency(m.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  const pieData = categories.slice(0, 8).map(c => ({ name: c.name, value: c.total }));
  const trendData = monthly_trend.map(m => ({ month: m.month, Total: m.total }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard title="Total Expenses" value={formatCurrency(grand_total)} icon={TrendingDown} color="red" />
        <KPICard title="Categories" value={categories.length} subtitle="expense categories" icon={PieChartIcon} color="orange" />
        <KPICard title="Transactions" value={transaction_count} subtitle="posted expenses" icon={Receipt} color="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {pieData.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Expense Breakdown</CardTitle></CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                      {pieData.map((entry, idx) => <Cell key={entry.name} fill={COLORS[idx % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {trendData.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Monthly Trend</CardTitle></CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#D94E18" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#D94E18" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={v => formatCurrency(v)} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="Total" stroke="#D94E18" fill="url(#expGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">By Category</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="report-table" data-testid="expense-category-table">
              <thead><tr><th className="pl-4">Category</th><th className="text-right">Amount</th><th className="text-right">%</th><th className="text-right pr-4">#</th></tr></thead>
              <tbody>
                {categories.map((c, idx) => (
                  <tr key={c.code}>
                    <td className="pl-4">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                        <span className="text-sm">{c.name}</span>
                      </div>
                    </td>
                    <td className="text-right font-mono">{formatCurrency(c.total)}</td>
                    <td className="text-right text-zinc-500 text-sm">{c.percentage}%</td>
                    <td className="text-right pr-4 text-zinc-400">{c.count}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t-2 border-zinc-200"><td className="font-semibold pl-4">Total</td><td className="text-right font-mono font-semibold">{formatCurrency(grand_total)}</td><td className="text-right text-zinc-500">100%</td><td></td></tr></tfoot>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Top Merchants</CardTitle></CardHeader>
          <CardContent>
            {top_merchants.length === 0 ? <p className="text-sm text-zinc-400">No data</p> : (
              <div className="space-y-3">
                {top_merchants.map((m, idx) => {
                  const pct = grand_total > 0 ? (m.total / grand_total * 100) : 0;
                  return (
                    <div key={m.name}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-zinc-700 truncate mr-4">{m.name}</span>
                        <span className="font-mono text-zinc-900 flex-shrink-0">{formatCurrency(m.total)}</span>
                      </div>
                      <div className="w-full bg-zinc-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: COLORS[idx % COLORS.length] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
